import { useCallback, useEffect, useRef } from 'react';

import { useDrivenVersionInForce } from './answer-ref-context';
import { pulseField, pulseVisibleField } from './field-reveal';
import { useVersionInForce } from './version-in-force';

import type { Block } from '@/types/experience';

/**
 * Everything a block needs to hold a question bank, so no block has to remember it.
 *
 * Eleven blocks still have to be wired, and the parts below are exactly the ones that were subtle
 * enough to get wrong once already in the first block that was: the two pointers were one, the
 * seeding ran on the instance that was clicked rather than the one on screen, and the pulse
 * pointed at whatever had not moved. Copied by hand eleven times, those are eleven chances to
 * reintroduce each — which is the failure the branch carousel's own doc records seven copies of.
 */

/**
 * The payload for a STRUCTURAL version write: adding or removing a version, where what is emitted
 * is a whole block rather than the handful of fields that changed.
 *
 * `onUpdate` REPLACES `block.data` wholesale, so a write is only as complete as the object handed
 * to it — and the object handed to it comes from the block's `initData`, which produces the
 * EDITOR's view of the block, not everything stored on it. Numerical threads feedback separately
 * and so `initData` emits no `feedback` key at all: pressing `+` on a block with three authored
 * feedback states replaced its data with a copy that had none, and the bank the teacher had just
 * created was the only thing they could see had worked.
 *
 * Merging onto the stored data restores exactly the keys `initData` never knew about. It cannot
 * resurrect something the write meant to clear, because `applyVersionHead` clears by ASSIGNING
 * `undefined` rather than deleting the key, and a spread carries that through.
 *
 * This is the same delete-by-omission that cost a whole question bank once already. Field writes
 * are covered by the `writeOptionsVersion` invariant; this is the other road to it.
 */
export function versionStructure<D>(stored: unknown, next: D): Record<string, unknown> {
  return { ...(stored as Record<string, unknown>), ...(next as unknown as Record<string, unknown>) };
}

/**
 * Which version the TEST surface and the EDITOR are each pointed at.
 *
 * TWO pointers, not one. Testing a bank and authoring it are different acts: stepping the test
 * panel used to re-seed every editor field from a version the teacher had never asked to open,
 * and because the editor's fields then fed the live test surface back, left the panel reporting
 * one version while showing another.
 *
 * COUPLED ONE WAY, and the direction is the whole of it. The bug above is TEST moving the
 * EDITOR, and that stays forbidden. The reverse is not the same act: authoring variant 2 and
 * then testing variant 1 is not a thing anyone asked for, and it reads as the feature being
 * broken — "I felt the feature was broken while I was editing scenario 2 because the test block
 * did not update visually". So moving the editor carries the test surface with it, and moving
 * the test surface leaves the editor alone.
 *
 * Scenario FOLLOWERS already behaved this way and that is why the inconsistency was visible:
 * a follower has only ever had one pointer (`useScenarioFollower` reads the leader's TEST
 * pointer for both its rows and its stem), so paging a follower's rows always moved its test
 * view. Only a block with its own BANK had two, which is why the leader was the block that
 * appeared stuck.
 */
/**
 * WHICH VERSION THE EDITOR IS ON — the one answer, for every reader of it.
 *
 * TWO readers, and they drifted. `useBlockVersions` overrides the stored pointer with the
 * driven one; `useDrivenRowInForce` read the stored pointer straight, which a driven block
 * never writes — so the box around the answer setup named the FIRST row for all four of them.
 * Cycling the driver moved the question, the answer key and the card, and left the heading
 * above them saying "Penguin" while the question underneath asked about a bear.
 *
 * So the resolution lives here and the override cannot be applied in one place and forgotten
 * in the other. `driven` is returned alongside because `VersionSelector` and the row frame
 * both need to know the pointer is not the teacher's to move, not just where it points.
 */
export function useEditorVersionInForce(block: Block | undefined) {
  /* `?? ''` so the hook is unconditional on a block that is not there yet — the store reads an
     unknown key as undefined, which is what an absent block should answer anyway. */
  const [raw, setRaw] = useVersionInForce(block?.id ?? '', 'editor');
  const driven = useDrivenVersionInForce(block);
  const versions = ((block?.data as { versions?: { id: string }[] } | undefined)?.versions ?? []);
  return {
    driven,
    setRaw,
    /*
     * BOTH SPELLINGS, or only half the editor follows.
     *
     * `editorVersion` is the raw pointer and `editorVersionId` resolves an absent one to the
     * first version; blocks read the first to decide when to RE-SEED their fields and the
     * second to label the pager. Overriding only the second moved the label and left the
     * question on screen where it was — the version the driver chose was named and not shown.
     */
    editorVersion: driven ?? raw,
    /** `pickVersion` reads an absent id as "the first version", and so does every label. */
    editorVersionId: driven ?? raw ?? versions[0]?.id,
  };
}

export function useBlockVersions(block: Block) {
  const [testVersion, setTestVersion] = useVersionInForce(block.id, 'test');
  const { driven, setRaw: setEditorVersionRaw, editorVersion, editorVersionId } = useEditorVersionInForce(block);
  /**
   * One call, both pointers. Wrapped here rather than at each pager so no caller can forget —
   * the display bank row, the input blocks' own selector and `addVariant` all go through it.
   *
   * `useTestResetOnVersionChange` then clears the test surface as it would for any other move
   * of the test pointer, which is right: a verdict belongs to the variant it was taken on.
   */
  const setEditorVersion = useCallback((id: string | undefined) => {
    setEditorVersionRaw(id);
    setTestVersion(id);
  }, [setEditorVersionRaw, setTestVersion]);

  const versions = ((block.data as { versions?: { id: string }[] } | undefined)?.versions ?? []);
  /**
   * A DRIVEN BLOCK HAS ONE POINTER, AND IT IS THE ANSWER — see `useEditorVersionInForce`,
   * which applies that override for every reader of the editor pointer rather than only here.
   *
   * The stored pointers are overridden rather than written, so switching the link off returns
   * the block to wherever the pager had it rather than to wherever the last earlier answer
   * happened to land.
   */
  const shownVersionId = driven ?? testVersion ?? versions[0]?.id;
  const hasBank = versions.length > 1;

  /**
   * Point at what MOVED in the editor, which is the only way to tell a successful jump from a
   * dead button.
   *
   * Ring the editor, not the card: the card is what the teacher is already looking at, and what
   * needs confirming is the thing they cannot see. WHICH field depends on what the block has — a
   * banked block moves its question, a branching block moves its condition, a block that is both
   * moves both — and ringing the question on a block with no versions was worse than nothing,
   * because the question did not move and the ring said it had.
   *
   * The ladder rings one field and claims a generation while it does, so a second call supersedes
   * the first and two cannot run at once. The branch is the one that needs it — it can sit behind
   * a closed feedback panel — and the question, which is always mounted in the editing view, is
   * rung directly. See the ordering note in the body for why the direct one goes first.
   */
  const ringEditorJump = useCallback(async (branchId?: string) => {
    if (!branchId) {
      if (hasBank) await pulseField('question', block.id);
      return;
    }
    /*
     * The QUESTION first, because it is the one that is always there.
     *
     * Ringing the branch first and the question after looked right and was not: when the branch
     * field is not mounted — branching switched off, its UI not surfaced — the ladder polls for
     * it until its deadline, and the question's ring waited out that whole second behind it. The
     * question needs no surface work, so it goes immediately and the ladder chases the branch.
     */
    const rang = hasBank && pulseVisibleField('question');
    await pulseField(branchId, block.id);
    // Only if it was not on screen a moment ago: the ladder may have opened the surface holding it.
    if (hasBank && !rang) pulseVisibleField('question');
  }, [block.id, hasBank]);

  return {
    versions, hasBank,
    testVersion, setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  };
}

/**
 * Clear the test surface whenever the TEST pointer moves.
 *
 * A test result belongs to the version it was taken on. Without this, answering version 1
 * correctly and stepping to version 2 left the verdict, the fired feedback and the typed answer
 * sitting under a different question — and version 2's answer key is, by the whole point of a
 * bank, a different one. The teacher who reported it put it plainly: otherwise it is misleading.
 *
 * Only fires on a CHANGE, never on mount, so a block that restores a student's prior answer keeps
 * it. In Preview nothing moves this pointer — a student's drawn version is read from the task roll
 * instead — so this is the editor's test surface only.
 */
export function useTestResetOnVersionChange(versionId: string | undefined, reset: () => void): void {
  const seen = useRef(versionId);
  const resetRef = useRef(reset);
  resetRef.current = reset;

  useEffect(() => {
    if (seen.current === versionId) return;
    seen.current = versionId;
    // Through the ref, so `reset` being a fresh closure on every render cannot put it in the
    // dependency list and clear the surface on every keystroke.
    resetRef.current();
  }, [versionId]);
}

/**
 * Re-seed this instance's editor fields whenever the editor's pointer moves, whoever moved it.
 *
 * A selected block is mounted TWICE — the canvas card and the side panel — and the pointer is
 * shared while the seeding writes each instance's own `useState`. So the test panel's Edit
 * button, which lives on the CARD, moved the pointer (the panel's heading duly jumped to
 * "Version 2 of 3") and seeded the card's invisible copy of the fields, while the panel the
 * teacher was looking at went on showing version 1's question under a heading naming version 2.
 *
 * Seeding from the POINTER rather than from the click is what makes every instance react,
 * whichever one was pressed.
 *
 * Returns the ref so a caller that seeds synchronously — switching version itself, or deleting
 * one, where the post-change data is in hand and `block.data` is not yet — can mark itself done
 * and keep this effect out of the way.
 */
export function useVersionSeeding(
  editorVersion: string | undefined,
  seed: (versionId: string | undefined) => void,
): React.MutableRefObject<string | undefined> {
  const seededRef = useRef(editorVersion);
  const seedRef = useRef(seed);
  seedRef.current = seed;

  useEffect(() => {
    if (seededRef.current === editorVersion) return;
    seededRef.current = editorVersion;
    seedRef.current(editorVersion);
    // Deliberately only the pointer: re-running this whenever the block's data changed would
    // re-seed the editor out from under live typing.
  }, [editorVersion]);

  return seededRef;
}
